/**
 * The rules for putting an image into an email, and the words used to state them.
 *
 * Every limit here mirrors `packages/contracts/src/email-images.ts` and is printed next to the
 * control it governs, before an operator can hit it. A picker that accepts a 9 MB photo and then
 * fails on upload has spent somebody's time and taught them nothing about what would have
 * worked. The API checks all of it again, because a console check is a suggestion to anything
 * holding a session token.
 *
 * Two of the rules are worth knowing the reason for:
 *
 *   - **No SVG.** It is a document format that can carry script and external references, mail
 *     clients that render it disagree about which parts they strip, and nothing an operator
 *     needs a banner for requires it.
 *   - **Alt text is required.** Most clients block images by default, so for a large share of
 *     recipients the alt text *is* the image. An operator made to type one sentence writes
 *     something useful; one who is not leaves a blank rectangle in every inbox.
 */
import type { EmailImage, EmailImageMime } from '../contracts/types.js';
import { EMAIL_IMAGE_MIMES } from '../contracts/vocab.js';

/** Mirrors `EMAIL_IMAGE_MAX_BYTES`. Chosen against how mail is delivered, not what a column holds. */
export const EMAIL_IMAGE_MAX_BYTES = 2_000_000;

/** The accept attribute for a file input, built from the one list of allowed types. */
export const EMAIL_IMAGE_ACCEPT = EMAIL_IMAGE_MIMES.join(',');

export const EMAIL_IMAGE_TYPE_LABEL = 'PNG, JPG, GIF or WebP';

export const EMAIL_IMAGE_ALT_MAX = 200;
export const EMAIL_IMAGE_LABEL_MAX = 120;

export function isEmailImageMime(mime: string): mime is EmailImageMime {
  return (EMAIL_IMAGE_MIMES as readonly string[]).includes(mime);
}

/**
 * Bytes, as a person reads them.
 *
 * Whole numbers above a megabyte would hide the difference between 1.1 MB and 1.9 MB when the
 * ceiling is 2, so the megabyte step keeps one decimal and the smaller ones do not need it.
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '0 B';
  if (bytes < 1024) return `${String(Math.round(bytes))} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${String(Math.round(kb))} KB`;
  const mb = kb / 1024;
  return `${(Math.round(mb * 10) / 10).toFixed(1)} MB`;
}

export type ImageCheck = { readonly ok: true } | { readonly ok: false; readonly error: string };

/**
 * Whether a chosen file may be uploaded.
 *
 * Every refusal names the limit and the actual value, because "file too large" leaves an
 * operator resizing an image by guesswork.
 */
export function checkImageFile(file: {
  readonly name: string;
  readonly type: string;
  readonly size: number;
}): ImageCheck {
  if (!isEmailImageMime(file.type)) {
    return {
      ok: false,
      error: `${EMAIL_IMAGE_TYPE_LABEL} only. That file is ${file.type === '' ? 'an unknown type' : file.type}.`,
    };
  }
  if (file.size <= 0) return { ok: false, error: 'That file is empty.' };
  if (file.size > EMAIL_IMAGE_MAX_BYTES) {
    return {
      ok: false,
      error: `Each image must be under ${formatBytes(EMAIL_IMAGE_MAX_BYTES)}. That one is ${formatBytes(file.size)}.`,
    };
  }
  return { ok: true };
}

/** Whether the alt text an operator typed will be accepted. Required, and the API agrees. */
export function checkAltText(altText: string): ImageCheck {
  const trimmed = altText.trim();
  if (trimmed === '') {
    return {
      ok: false,
      error: 'Describe the image. Most mail clients block images, so this is what those readers get instead.',
    };
  }
  if (trimmed.length > EMAIL_IMAGE_ALT_MAX) {
    return { ok: false, error: `Keep the description under ${String(EMAIL_IMAGE_ALT_MAX)} characters.` };
  }
  return { ok: true };
}

/** What a set of images weighs. */
export function totalBytes(images: readonly EmailImage[]): number {
  return images.reduce((sum, image) => sum + image.byteSize, 0);
}

/**
 * The `<img>` tag for an image, ready to paste into a body.
 *
 * `max-width:100%` and `height:auto` inline rather than in a stylesheet, because a mail client
 * that strips `<style>` is the normal case rather than the exception, and a fixed-width image in
 * a phone's mail app is a message with a horizontal scrollbar.
 *
 * Returns null when the image has no URL. `EmailImage.url` is nullable by contract: a deployment
 * with no public address configured stores the bytes but cannot reference them, and a tag
 * pointing at nothing is a broken rectangle in every inbox rather than a small inconvenience.
 */
export function imageTagFor(image: EmailImage): string | null {
  if (image.url === null || image.url === '') return null;
  const alt = image.altText.replace(/["\\<>]/g, '');
  return `<img src="${image.url}" alt="${alt}" style="max-width:100%;height:auto;display:block;border:0;" />`;
}

/**
 * The images a message actually uses, out of everything in the library.
 *
 * Derived from the markup rather than tracked as a separate list, because the markup is the only
 * thing that decides. An operator who deletes the `<img>` tag by hand has removed the image; a
 * parallel "attached" list would still claim it was there.
 */
export function referencedImages(
  html: string,
  images: readonly EmailImage[],
): readonly EmailImage[] {
  return images.filter((image) => image.url !== null && image.url !== '' && html.includes(image.url));
}

/**
 * Read a picked file as base64, without the `data:` prefix.
 *
 * Browser-only by construction (`FileReader`), which is why it is a function rather than anything
 * that runs at module load: the rules above are tested in node, and importing them must not
 * require a DOM.
 */
export function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read that file.'));
    reader.onload = () => {
      const result = String(reader.result);
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}
